"use client";

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Star, Book, Zap, Droplets, Shield, Coins } from 'lucide-react';
import { AboutLayout } from '@/components/about/about-layout';

export default function GoldenDawnMinorArcanaPage() {
  const suits = [
    {
      name: "Wands",
      element: "Ateş",
      description: "Ruhsal güç, yaratıcı enerji ve Kabalistik ateş elementi",
      icon: <Zap className="h-6 w-6 text-white" />,
      color: "from-amber-500 to-orange-700",
      path: "wands",
      keywords: ["Ruhsal güç", "Yaratıcılık", "İlham", "Enerji"]
    },
    {
      name: "Cups",
      element: "Su",
      description: "Duygular, aşk, sezgi ve Kabalistik su elementi",
      icon: <Droplets className="h-6 w-6 text-white" />,
      color: "from-amber-400 to-blue-700",
      path: "cups",
      keywords: ["Aşk", "Duygular", "Sezgi", "Ruhsal bağlantı"]
    },
    {
      name: "Swords",
      element: "Hava",
      description: "Zihin, düşünce, iletişim ve Kabalistik hava elementi",
      icon: <Shield className="h-6 w-6 text-white" />,
      color: "from-amber-300 to-gray-700",
      path: "swords",
      keywords: ["Zihin", "Mantık", "İletişim", "Adalet"]
    },
    {
      name: "Pentacles",
      element: "Toprak",
      description: "Maddi dünya, para, sağlık ve Kabalistik toprak elementi",
      icon: <Coins className="h-6 w-6 text-white" />,
      color: "from-amber-600 to-green-700",
      path: "pentacles",
      keywords: ["Para", "Sağlık", "İş", "Maddi başarı"]
    }
  ];

  return (
    <AboutLayout activeSection="tarot">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Current Section Indicator */}
        <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
          <div className="flex items-center gap-2 text-amber-300">
            <Star className="h-4 w-4" />
            <span className="text-sm font-medium">🃏 Tarot</span>
            <span className="text-amber-500">/</span>
            <span className="text-sm">Golden Dawn</span>
            <span className="text-amber-500">/</span>
            <span className="text-sm">Minör Arkana</span>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="mb-6">
          <Link
            href="/about"
            className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            AXTROLOG Nedir
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <Link
            href="/about/tarot/systems"
            className="text-slate-400 hover:text-slate-200 transition-colors"
          >
            Tarot Sistemleri
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <Link
            href="/about/tarot/systems/golden-dawn"
            className="text-slate-400 hover:text-slate-200 transition-colors"
          >
            Golden Dawn Tarot
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <span className="text-slate-300">Minör Arkana</span>
        </div>

        {/* Header */}
        <div className="bg-gradient-to-r from-amber-900/20 to-yellow-900/20 rounded-xl p-8 mb-8 border border-amber-500/20">
          <h1 className="text-4xl font-bold text-slate-100 mb-4 flex items-center gap-3">
            <Book className="h-10 w-10 text-amber-400" />
            Golden Dawn Minör Arkana
          </h1>
          <p className="text-lg text-slate-200 leading-relaxed">
            56 karttan oluşan Minör Arkana, Hermetic Order of the Golden Dawn'un derinlemesine Kabalistik ve elemental sistemine göre tasarlanmış.
            Her kart, Tree of Life'daki Sephirot'lar ve astrolojik correspondence'larla güçlü bir bağlantı kurar.
          </p>
        </div>

        {/* Golden Dawn Court Cards */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Golden Dawn Court Kartları Sistemi</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-slate-100 mb-3">Geleneksel İsimlendirme</h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• <strong>Page</strong> - Genç enerji, öğrenme aşaması</li>
                <li>• <strong>Knight</strong> - Hareket halinde enerji, eylem</li>
                <li>• <strong>Queen</strong> - Dişil enerji, kabul edici güç</li>
                <li>• <strong>King</strong> - Eril enerji, yaratıcı güç</li>
              </ul>
            </div>
            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-slate-100 mb-3">Kabalistik Correspondence</h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Page: Malkuth'daki elemental güç</li>
                <li>• Knight: Yetzirah dünyasındaki dinamik enerji</li>
                <li>• Queen: Briah dünyasındaki yaratıcı güç</li>
                <li>• King: Atziluth dünyasındaki arketipsel enerji</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Four Suits */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Dört Suit</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {suits.map((suit, index) => (
              <div key={index} className="bg-slate-800 rounded-xl p-6 border border-slate-700 hover:border-amber-500/50 transition-colors">
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-12 h-12 bg-gradient-to-br ${suit.color} rounded-lg flex items-center justify-center`}>
                    {suit.icon}
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-slate-100">{suit.name}</h3>
                    <p className="text-sm text-slate-400">{suit.element} elementi</p>
                  </div>
                </div>
                <p className="text-slate-300 mb-4">{suit.description}</p>
                <div className="flex flex-wrap gap-2 mb-4">
                  {suit.keywords.map((keyword, i) => (
                    <span key={i} className="px-2 py-1 bg-slate-700 text-slate-300 rounded text-xs">
                      {keyword}
                    </span>
                  ))}
                </div>
                <Link
                  href={`/about/tarot/systems/golden-dawn/minor-arcana/${suit.path}`}
                  className={`inline-flex items-center px-4 py-2 bg-gradient-to-r ${suit.color} text-white rounded-lg transition-colors text-sm hover:shadow-lg`}
                >
                  <Book className="h-4 w-4 mr-2" />
                  {suit.name} Kartlarını Gör
                </Link>
              </div>
            ))}
          </div>
        </div>

        {/* Kabbalistic Correspondences */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Kabalistik Correspondence'lar</h2>
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold text-slate-100 mb-3">Tree of Life Bağlantıları</h3>
                <ul className="space-y-2 text-slate-300 text-sm">
                  <li>• <strong>Ace:</strong> Kether - saf element gücü</li>
                  <li>• <strong>2:</strong> Chokmah - yaratıcı güç</li>
                  <li>• <strong>3:</strong> Binah - form ve yapı</li>
                  <li>• <strong>4:</strong> Chesed - merhamet ve genişleme</li>
                  <li>• <strong>5:</strong> Geburah - güç ve disiplin</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-100 mb-3">Astrolojik Sistemat</h3>
                <ul className="space-y-2 text-slate-300 text-sm">
                  <li>• <strong>6:</strong> Tiphareth - güneş ve denge</li>
                  <li>• <strong>7:</strong> Netzach - Venüs ve zafer</li>
                  <li>• <strong>8:</strong> Hod - Merkür ve iletişim</li>
                  <li>• <strong>9:</strong> Yesod - Ay ve temel</li>
                  <li>• <strong>10:</strong> Malkuth - tamamlanma</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Elemental Dignities */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Elemental Dignities</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-slate-100 mb-3">Uyumlu Elementler</h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• <strong>Ateş & Hava:</strong> Güçlendirici etki</li>
                <li>• <strong>Su & Toprak:</strong> Destekleyici enerji</li>
                <li>• Aynı elementler: Güçlü rezonans</li>
                <li>• Komşu elementler: Karışık etkiler</li>
              </ul>
            </div>
            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-slate-100 mb-3">Zıt Elementler</h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• <strong>Ateş & Su:</strong> Zıtlık ve çatışma</li>
                <li>• <strong>Hava & Toprak:</strong> Dengesizlik</li>
                <li>• Zıt kartlar: Güçlü gerilim</li>
                <li>• Okuma sırasında dikkat gerektirir</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Traditional Meanings */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Geleneksel Golden Dawn Yaklaşımı</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-slate-100 mb-3">Sistematik Yaklaşım</h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Her kartın kesin astrolojik correspondence'ı</li>
                <li>• Decanlar sistemi (10°'lik bölümler)</li>
                <li>• Planetary ruler'lar ve etkileri</li>
                <li>• Zodiac işaretleri ile bağlantı</li>
                <li>• Hebrew harfleri ile kodlama</li>
              </ul>
            </div>
            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-slate-100 mb-3">Okuma Teknikleri</h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Elemental dignities analizi</li>
                <li>• Tree of Life spread'leri</li>
                <li>• Kabalistik meditation</li>
                <li>• Astrolojik timing</li>
                <li>• Pathworking ile derin yorumlama</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Important Note */}
        <div className="bg-amber-900/20 border border-amber-700/50 rounded-xl p-6 mb-8">
          <h3 className="text-lg font-semibold text-amber-200 mb-3">⚠️ Önemli Uyarı</h3>
          <p className="text-amber-100 leading-relaxed">
            Bu kart anlamları Golden Dawn'un geleneksel öğretilerine dayanmaktadır ve sadece eğlence ve kişisel farkındalık amaçlıdır.
            Yaşamsal kararlar için uzman görüşü alınmalıdır.
          </p>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Link
            href="/about/tarot/systems/golden-dawn/major-arcana"
            className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Majör Arkana
          </Link>

          <Link
            href="/about/tarot/systems/golden-dawn"
            className="inline-flex items-center px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-colors"
          >
            Golden Dawn Tarot
            <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
          </Link>
        </div>
      </div>
    </AboutLayout>
  );
}